/**
 * Resolve the portrait/preview image URL for a small wheel CARD (gallery,
 * gallery list-view, explore feed) — not the full wheel view.
 *
 * ISP-9999: a VIDEO portrait (VIDEO_PORTRAIT_ENABLED) stores its raw asset as
 * an .mp4 in `wheel_image` (get_wheel_image folds center_media.url — the mp4 —
 * into wheel_image for a video wheel). The full ViewWheel renders that fine
 * (WheelComponent overlays a real <video>), but a card's createPie <img> can't
 * decode an .mp4 — blank/broken center (naturalWidth/Height 0, MEASURED on
 * web-stg 2026-08-19: clone 4830 mp4-as-<img> → onerror naturalWidth 0; the
 * poster still → onload 160×100). So a video portrait must resolve to its STILL
 * frame, never the raw video url.
 *
 * FIELD NAME (QA-148 #8, MEASURED 2026-08-19): the backend serializes the video
 * still under `wheel_center_media.poster` — the canonical media contract shared
 * with the builder is `{ type, source, poster }` (see utils/portraitMedia.js).
 * An earlier revision of this helper read `thumbnail`, a key the API never
 * emits, so the video branch NEVER fired: every video-portrait card (and every
 * cloned video wheel in the gallery "Cloned" section) fell through to the raw
 * mp4 and rendered a broken center. Prefer `thumbnail` when a payload does carry
 * it (older/alternate shape) but fall back to `poster`, and finally to the
 * caller's logo — NEVER to `wheel_image` (the mp4) for a video.
 *
 * Deliberately never returns the raw video url here — a card thumbnail must not
 * autoplay; a static still is the correct card treatment.
 *
 * @param {object} data - a wheel-list API item (or explore-feed item).
 * @param {string} [fallback] - url to use when there is no usable still
 *   (mirrors each call site's own existing `|| ASSETS.wheelLogoFallback` behavior).
 * @returns {string|undefined}
 */
export function resolveWheelCardPortraitUrl(data, fallback) {
  const media = data?.wheel_center_media;
  if (media?.type === "video") {
    // A card <img> cannot decode an .mp4 — use the still frame the API provides.
    // `poster` is canonical; `thumbnail` is an older/alternate key. Never fall
    // through to wheel_image (the raw video url) for a video portrait.
    return media.thumbnail || media.poster || fallback;
  }
  return data?.wheel_image || fallback;
}
